import { useState } from 'react';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader } from '../components/ui/Card';
import { DataTable, TableToolbar, Pagination } from '../components/ui/DataTable';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { Tabs, Modal, useToast } from '../components/ui/Overlay';
import { Button } from '../components/ui/Button';
import { Field, Input, Select, SearchInput } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { userColumns } from '../components/domain/columns';
import {
  EditRoleModal, RemovePersonModal, DecideRequestModal, assignableRoles, requestTarget,
} from '../components/domain/PersonModals';
import { OrgField, useOrgField } from '../components/domain/OrgField';
import { ManageFleetModal, VehicleAccessModal } from '../components/domain/FleetModals';
import { Icon } from '../components/ui/Icon';
import { Can } from '../auth/Can';
import { useAuth } from '../auth/AuthProvider';
import { useResource, useMutation, usePagedList } from '../hooks/useResource';
import { api } from '../lib/api';
import { ROLES, roleMeta } from '../lib/domain';
import * as fmt from '../lib/format';

/**
 * Administration — people and fleets.
 *
 * The role list a person can grant comes from what they hold themselves: an
 * organisation admin can create managers and viewers, a manager can create
 * viewers. The server enforces exactly the same rule, so this only decides
 * what to show, never what is permitted.
 */
export function Admin() {
  const [tab, setTab] = useState('users');
  const { can } = useAuth();

  // The pending count belongs on the tab — a queue nobody notices is a queue
  // that does not work.
  const pending = useResource('/api/access-requests', { state: 'PENDING', limit: 100 }, { pollMs: 60_000 });
  const pendingCount = pending.data?.requests?.length || 0;

  const tabs = [
    { value: 'users', label: 'People', icon: 'users' },
    { value: 'requests', label: 'Requests', icon: 'bell', count: pendingCount || undefined },
    ...(can('fleet:write') ? [{ value: 'fleets', label: 'Fleets', icon: 'truck' }] : []),
  ];

  return (
    <Page className="space-y-4">
      <PageHeader title="Administration" subtitle="People, access requests and fleets" />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'users' && <UsersPanel />}
      {tab === 'requests' && <RequestsPanel resource={pending} />}
      {tab === 'fleets' && <FleetsPanel />}
    </Page>
  );
}

/* --------------------------------------------------------------- requests -- */

function RequestsPanel({ resource }) {
  const [deciding, setDeciding] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const history = useResource(showAll ? '/api/access-requests' : null, { state: '', limit: 100 });
  const active = showAll ? history : resource;

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader
          title={showAll ? 'All access requests' : 'Waiting for a decision'}
          subtitle="People asking for access they do not have"
          icon="bell"
          actions={
            <Button variant="ghost" size="sm" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Pending only' : 'Show decided'}
            </Button>
          }
        />
        <AsyncBoundary
          state={active}
          skeleton={<SkeletonRows rows={4} />}
          isEmpty={(data) => !data.requests.length}
          empty={
            <EmptyState
              icon="check"
              title="Nothing waiting"
              description="Requests appear here when someone asks for a vehicle, a fleet or a role."
            />
          }
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.requests.map((request) => (
                <li key={request.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-medium text-ink">
                      {request.requester_name}
                      <span className="font-normal text-ink-3"> wants </span>
                      {requestTarget(request)}
                    </p>
                    <p className="truncate text-[12px] text-ink-3">
                      {roleMeta(request.requester_role).label} · asked {fmt.relativeTime(request.created_at)}
                      {request.reason ? ` · “${request.reason}”` : ''}
                    </p>
                  </div>

                  {request.state === 'PENDING' ? (
                    <div className="flex shrink-0 items-center gap-2">
                      {!request.canDecide && (
                        <span
                          className="flex items-center gap-1 text-[12px] text-ink-3"
                          title={request.cannotDecideReason}
                        >
                          <Icon name="shield" size="xs" />
                          not yours to grant
                        </span>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => setDeciding(request)}>
                        Review
                      </Button>
                    </div>
                  ) : (
                    <Badge
                      tone={
                        { APPROVED: 'good', DENIED: 'critical', CANCELLED: 'neutral' }[request.state] ||
                        'neutral'
                      }
                      size="sm"
                      icon={null}
                    >
                      {request.state.toLowerCase()}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <DecideRequestModal
        open={Boolean(deciding)}
        request={deciding}
        onClose={() => setDeciding(null)}
        onDecided={() => {
          setDeciding(null);
          resource.refetch();
          if (showAll) history.refetch();
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ users -- */

function UsersPanel() {
  const toast = useToast();
  const { user: me, can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [granting, setGranting] = useState(null);

  // Searched and paged on the server: a thousand-person organisation is a
  // list you navigate, not one you scroll.
  const users = usePagedList('/api/users', null, { limit: 50 });
  const reactivate = useMutation(async (id) => api.post(`/api/users/${id}/reactivate`, {}));

  // Only offer roles this account is actually allowed to grant.
  const assignable = assignableRoles(me?.role);

  /**
   * Row actions. Shown only for people this account could actually act on —
   * offering "Change role" on someone you cannot touch just produces a 403
   * when they click it.
   */
  const actionsColumn = {
    key: 'actions',
    header: '',
    align: 'right',
    render: (row) => {
      const isSelf = row.id === me?.id;
      const actionable = !isSelf && assignable.includes(row.role);
      if (!actionable) {
        return <span className="text-[12px] text-ink-3">{isSelf ? 'you' : '—'}</span>;
      }

      return (
        <div className="flex items-center justify-end gap-1">
          {/* A viewer's scope is a list of individual vehicles, so this is the
              only place their access is ever granted directly. Nobody else has
              per-vehicle grants — a manager's scope comes from their fleets. */}
          {can('access:grant') && row.role === 'USER' && (
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); setGranting(row); }}
              className="rounded-md px-2 py-1 text-[12.5px] font-medium text-brand hover:bg-brand-soft"
            >
              Vehicles
            </button>
          )}
          {can('user:role') && (
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); setEditing(row); }}
              className="rounded-md px-2 py-1 text-[12.5px] font-medium text-brand hover:bg-brand-soft"
            >
              Role
            </button>
          )}
          {can('user:disable') &&
            (row.status === 'ACTIVE' ? (
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); setRemoving(row); }}
                className="rounded-md px-2 py-1 text-[12.5px] font-medium text-critical-ink hover:bg-critical-soft"
              >
                Remove
              </button>
            ) : (
              <button
                type="button"
                onClick={async (event) => {
                  event.stopPropagation();
                  try {
                    const result = await reactivate.run(row.id);
                    toast.success(result?.message || 'Account restored.');
                    users.refetch();
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                className="rounded-md px-2 py-1 text-[12.5px] font-medium text-good-ink hover:bg-good-soft"
              >
                Restore
              </button>
            ))}
        </div>
      );
    },
  };

  return (
    <>
      <Card className="overflow-hidden">
        <TableToolbar>
          <SearchInput
            value={users.query}
            onChange={users.setQuery}
            placeholder="Name or email"
            className="w-full sm:w-64"
          />
          <span className="text-[13px] text-ink-2 tabular-nums">
            {users.total.toLocaleString()} account{users.total === 1 ? '' : 's'}
          </span>
          <Can do="user:create">
            <Button
              variant="accent"
              size="sm"
              icon="plus"
              className="ml-auto"
              onClick={() => setCreating(true)}
              disabled={!assignable.length}
            >
              Add person
            </Button>
          </Can>
        </TableToolbar>

        <AsyncBoundary
          state={users}
          skeleton={<SkeletonRows rows={6} />}
          isEmpty={() => !users.rows.length}
          empty={
            <EmptyState
              icon="users"
              title={users.query ? 'Nobody matches that search' : 'No people to show'}
            />
          }
        >
          {() => (
            <>
              <DataTable
                columns={[...userColumns(), actionsColumn]}
                rows={users.rows}
                defaultSort={{ key: 'full_name', direction: 'asc' }}
                paged={users.total > users.page.limit}
              />
              <Pagination page={users.page} onChange={users.setOffset} unit="people" />
            </>
          )}
        </AsyncBoundary>
      </Card>

      <CreateUserModal
        open={creating}
        assignable={assignable}
        onClose={() => setCreating(false)}
        onCreated={() => {
          toast.success('Account created.');
          setCreating(false);
          users.refetch();
        }}
      />

      <EditRoleModal
        open={Boolean(editing)}
        person={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          users.refetch();
        }}
      />

      <RemovePersonModal
        open={Boolean(removing)}
        person={removing}
        onClose={() => setRemoving(null)}
        onRemoved={() => {
          setRemoving(null);
          users.refetch();
        }}
      />

      <VehicleAccessModal
        open={Boolean(granting)}
        person={granting}
        onClose={() => setGranting(null)}
      />
    </>
  );
}

function CreateUserModal({ open, assignable, onClose, onCreated }) {
  const toast = useToast();
  const org = useOrgField();
  const [form, setForm] = useState({ fullName: '', email: '', password: '', role: 'USER', orgId: '' });

  // A platform administrator belongs to no organisation, so an account they
  // create has to name one — except another platform administrator, who also
  // belongs to none.
  const needsOrg = org.visible && form.role !== 'SUPER_ADMIN';

  const create = useMutation(async () => {
    await api.post('/api/users', {
      ...form,
      orgId: needsOrg ? form.orgId : undefined,
    });
  });

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async () => {
    try {
      await create.run();
      setForm({ fullName: '', email: '', password: '', role: 'USER', orgId: '' });
      onCreated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a person"
      description="They will be able to sign in immediately with this password."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            onClick={submit}
            disabled={
              !form.fullName || !form.email || form.password.length < 10 || (needsOrg && !form.orgId)
            }
          >
            Create account
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Full name" required>
          <Input value={form.fullName} onChange={set('fullName')} placeholder="Ravi Menon" />
        </Field>
        <Field label="Email" required>
          <Input type="email" value={form.email} onChange={set('email')} placeholder="ravi@company.com" />
        </Field>
        <Field
          label="Temporary password"
          required
          hint="At least 10 characters. Ask them to change it after signing in."
          error={form.password && form.password.length < 10 ? 'Use at least 10 characters.' : null}
        >
          <Input type="text" value={form.password} onChange={set('password')} />
        </Field>
        <Field label="Role" hint={roleHint(form.role)}>
          <Select
            value={form.role}
            onChange={set('role')}
            options={assignable.map((role) => ({ value: role, label: ROLES[role].label }))}
          />
        </Field>

        {needsOrg && (
          <OrgField
            value={form.orgId}
            onChange={(value) => setForm((current) => ({ ...current, orgId: value }))}
            organizations={org.organizations}
            loading={org.loading}
            hint="The organisation this person will work in. They will see nothing outside it."
          />
        )}
      </div>
    </Modal>
  );
}

function roleHint(role) {
  return {
    SUPER_ADMIN: 'Full access to every organisation on the platform.',
    ORG_ADMIN: 'Runs this organisation: fleets, people, devices and rules.',
    MANAGER: 'Day-to-day operations for the fleets they are assigned to.',
    USER: 'Read-only, and only for the vehicles they are granted.',
  }[role];
}

/* ----------------------------------------------------------------- fleets -- */

function FleetsPanel() {
  const toast = useToast();
  const org = useOrgField();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [managing, setManaging] = useState(null);

  const fleets = useResource('/api/fleets');
  const create = useMutation(async () => {
    await api.post('/api/fleets', { name, orgId: org.visible ? orgId : undefined });
  });

  const submit = async () => {
    try {
      await create.run();
      toast.success('Fleet created.');
      setName('');
      setOrgId('');
      setCreating(false);
      fleets.refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader
          title="Fleets"
          subtitle="Depots, regions or contracts — a manager is scoped to these"
          icon="truck"
          actions={
            <Can do="fleet:write">
              <Button variant="accent" size="sm" icon="plus" onClick={() => setCreating(true)}>
                New fleet
              </Button>
            </Can>
          }
        />
        <AsyncBoundary
          state={fleets}
          skeleton={<SkeletonRows rows={4} />}
          isEmpty={(data) => !data.fleets.length}
          empty={
            <EmptyState
              icon="truck"
              title="No fleets yet"
              description="Create a fleet, then move vehicles into it and assign a manager."
            />
          }
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.fleets.map((fleet) => (
                <li key={fleet.id}>
                  {/* The whole row opens the fleet, because assigning a manager
                      is the reason most people come to this list at all — a
                      fleet with no manager is a fleet nobody can see. */}
                  <button
                    type="button"
                    onClick={() => setManaging(fleet)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-medium text-ink">{fleet.name}</p>
                      <p className="truncate text-[12.5px] text-ink-3">
                        {fleet.description || fleet.timezone}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge tone="brand" size="sm" icon={null}>
                        {fleet.vehicle_count} vehicle{Number(fleet.vehicle_count) === 1 ? '' : 's'}
                      </Badge>
                      <Badge
                        tone={Number(fleet.manager_count) === 0 ? 'warning' : 'neutral'}
                        size="sm"
                        icon={null}
                      >
                        {Number(fleet.manager_count) === 0
                          ? 'no manager'
                          : `${fleet.manager_count} manager${Number(fleet.manager_count) === 1 ? '' : 's'}`}
                      </Badge>
                      <Icon name="arrow" size="xs" className="text-ink-3" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New fleet"
        description="Vehicles and managers are assigned to a fleet, which is what scopes a manager's access."
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={create.pending}
              onClick={submit}
              disabled={!name.trim() || (org.visible && !orgId)}
            >
              Create fleet
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Fleet name" required>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Bengaluru Depot" />
          </Field>

          {org.visible && (
            <OrgField
              value={orgId}
              onChange={setOrgId}
              organizations={org.organizations}
              loading={org.loading}
            />
          )}
        </div>
      </Modal>

      <ManageFleetModal
        open={Boolean(managing)}
        fleet={managing}
        onClose={() => setManaging(null)}
        onChanged={(opts) => {
          fleets.refetch();
          // A manager assignment keeps the modal open — you usually assign two
          // or three people in a row — so only an edit or a delete closes it.
          if (!opts?.silent) setManaging(null);
        }}
      />
    </>
  );
}
